import { hoursFor } from '../components/planningboard/boardRows.js'

// FAR-77 §5.6, as the engine's unitsByPercent: rounded up to the lot (or the
// case), never below 0.
export function unitsByPercent(units, pct, lot) {
  const v = (units || 0) * (1 + pct / 100)
  if (v <= 0) return 0
  if (lot > 0) return Math.ceil(v / lot - 1e-9) * lot
  return Math.ceil(v - 1e-9)
}

function matchesScope(mod, row) {
  if (mod.scope === 'AllProduct') return true
  if (mod.scope === 'BriteCode') return String(row.briteCode) === String(mod.identifier)
  if (mod.scope === 'ItemNumber') return String(row.itemNumber) === String(mod.identifier)
  if (mod.scope === 'Items') return (mod.items || []).map(String).includes(String(row.itemNumber))
  return false
}

// The value a modification sets for one month. monthValues are keyed by ISO
// month ("2026-09"), as the engine stores them; defaultValue applies otherwise.
function getMonthValue(mod, month) {
  const v = mod.monthValues?.[month.iso]
  if (v !== undefined && v !== null) return v
  if (mod.defaultValue !== undefined && mod.defaultValue !== null) return mod.defaultValue
  return null
}

/**
 * Apply modifications to base row data and return modified rows (preview only;
 * the engine is the source of truth once a plan is recalculated).
 *
 * FAR-72: months come from the caller (the plan's own months), not a hardcoded
 * Apr-Sep 2026 list, and hours use each item's real run rate rather than
 * made-up defaults (cpm 400, efficiency 0.85, pack 12).
 *
 * Modifications apply oldest first (highest listPosition first -- a new one
 * goes on top as position 1), so the newest wins where two touch the same
 * cell (FAR-78 Open Item 2). Each operates on the RESULT of the previous one.
 *
 * PercentOfMinInv: CASES = previousCases * value (e.g., 1.30 = 130% of previous)
 * Qty: CASES = previousCases + value (can be negative, floor at 0)
 * Cell (FAR-76): CASES = the units the planner typed for that month. Absolute,
 *   and applied after every other modification, as the engine does.
 */
export function applyModifications(baseRows, modifications, mode = 'modified', months = []) {
  if (mode === 'base' || !modifications || modifications.length === 0 || months.length === 0) {
    return baseRows
  }

  const activeMods = modifications
    .filter((m) => !m.isIgnored)
    .sort((a, b) => (b.listPosition || 0) - (a.listPosition || 0))
    // Cell edits last, whatever their list position (stable sort).
    .sort((a, b) => (a.changeType === 'Cell') - (b.changeType === 'Cell'))

  if (activeMods.length === 0) return baseRows

  return baseRows.map((baseRow) => {
    const row = { ...baseRow }
    let anyModified = false

    for (const month of months) {
      const mk = month.key
      const baseCases = baseRow[`${mk}_cases`] || 0
      let currentCases = baseCases
      let wasModified = false
      let lastPctValue = null
      let cellEdited = false

      for (const mod of activeMods) {
        if (!matchesScope(mod, row)) continue
        if (mod.changeType === 'UnitsPercent') {
          // FAR-77 §5.6: units x (1 + p), up to the lot, for the months it
          // names. As the engine (unitsByPercent).
          const p = mod.monthValues?.[month.iso]
          if (p === undefined || p === null) continue
          currentCases = unitsByPercent(currentCases, p, row.roundingValue)
          wasModified = true
          continue
        }
        if (mod.changeType === 'Cell') {
          // Only the months it names; 0 is a real value.
          const v = mod.monthValues?.[month.iso]
          if (v === undefined || v === null) continue
          currentCases = Math.max(0, v)
          wasModified = true
          cellEdited = true
          continue
        }
        // DOSTarget needs running inventory: the engine works it out (the
        // preview shows it after the recalculation). Explicit, now that
        // Items-scoped rules match rows.
        if (mod.changeType !== 'PercentOfMinInv' && mod.changeType !== 'Qty') continue
        const value = getMonthValue(mod, month)
        if (value === null) continue

        if (mod.changeType === 'PercentOfMinInv') {
          currentCases = Math.round(currentCases * value)
          wasModified = true
          lastPctValue = value
        } else if (mod.changeType === 'Qty') {
          currentCases = Math.max(0, currentCases + value)
          wasModified = true
        }
      }

      if (!wasModified) continue

      row[`${mk}_cases`] = currentCases
      row[`${mk}_scenarioModified`] = true
      row[`${mk}_hrs`] = hoursFor(currentCases, row.runRateCasesPerHour)
      if (lastPctValue !== null) row[`${mk}_pctMinInv`] = lastPctValue
      if (cellEdited) row[`${mk}_cellEdited`] = true
      anyModified = true
    }

    // DOS is left as the engine computed it: re-deriving it here needs the
    // running inventory and working days, which only the engine has. A
    // recalculation (doscalc) produces the authoritative DOS.
    if (anyModified) row.hasModifications = true
    return row
  })
}
